<script setup>
import { ref, reactive, watch, onMounted, onBeforeUnmount } from 'vue';
import MetricaPendingApprovalsAPI from 'dashboard/api/metrica/pendingApprovals';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const loading = ref(false);
const error = ref(false);
const items = ref([]);
// Per-item id, so approving one doesn't disable the others.
const actingOn = reactive({});
let pollTimer = null;

const detailsText = item => {
  if (!item.input || Object.keys(item.input).length === 0) return '';
  return JSON.stringify(item.input);
};

const fetchItems = async (opts = {}) => {
  if (!props.conversationId) return;
  const silent = opts.silent === true;
  if (!silent) {
    loading.value = true;
    error.value = false;
  }
  try {
    const { data } = await MetricaPendingApprovalsAPI.list(
      props.conversationId
    );
    items.value = data;
    if (silent) error.value = false;
  } catch (e) {
    if (silent) return; // keep the current list on background polls
    error.value = true;
  } finally {
    if (!silent) loading.value = false;
  }
};

const resolve = async (item, action) => {
  if (actingOn[item.id]) return; // already in flight, no double click
  actingOn[item.id] = true;
  try {
    if (action === 'approve') {
      await MetricaPendingApprovalsAPI.approve(props.conversationId, item.id);
    } else {
      await MetricaPendingApprovalsAPI.reject(props.conversationId, item.id);
    }
    // The item is resolved server-side (no longer 'pending') — drop it from
    // THIS list locally instead of waiting for the next poll.
    items.value = items.value.filter(i => i.id !== item.id);
  } catch (e) {
    // 409 = someone else already resolved it (or it's stale) — refetch so
    // the list reflects reality instead of showing a stale row.
    fetchItems();
  } finally {
    delete actingOn[item.id];
  }
};
const approve = item => resolve(item, 'approve');
const reject = item => resolve(item, 'reject');

watch(
  () => props.conversationId,
  () => fetchItems()
);

onMounted(() => {
  fetchItems();
  // Pending requests are time-sensitive (the customer is waiting on a
  // reply) — poll a bit faster than the analysis panel's 30s.
  pollTimer = setInterval(() => fetchItems({ silent: true }), 15000);
});
onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer);
});
</script>

<template>
  <div class="px-4 py-3 text-sm text-n-slate-12">
    <div v-if="loading" class="py-2 text-n-slate-10">
      {{ $t('METRICA_PENDING_APPROVALS.LOADING') }}
    </div>

    <div v-else-if="error" class="py-2 text-n-slate-10">
      {{ $t('METRICA_PENDING_APPROVALS.ERROR') }}
    </div>

    <div v-else-if="!items.length" class="py-2 text-n-slate-10">
      {{ $t('METRICA_PENDING_APPROVALS.EMPTY') }}
    </div>

    <div v-else class="flex flex-col gap-3">
      <div
        v-for="item in items"
        :key="item.id"
        class="flex flex-col gap-2 p-3 border rounded-lg border-n-strong bg-n-alpha-1"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="text-xs font-semibold text-n-slate-12">
            {{ item.action_type }}
          </span>
          <span v-if="item.agent_name" class="text-xs text-n-slate-10">
            {{ item.agent_name }}
          </span>
        </div>
        <div
          v-if="detailsText(item)"
          class="font-mono text-xs break-all text-n-slate-10"
        >
          {{ detailsText(item) }}
        </div>
        <div class="flex gap-2 mt-1">
          <Button
            variant="faded"
            color="teal"
            size="xs"
            icon="i-lucide-check"
            :is-loading="!!actingOn[item.id]"
            :disabled="!!actingOn[item.id]"
            :label="$t('METRICA_PENDING_APPROVALS.APPROVE')"
            @click="approve(item)"
          />
          <Button
            variant="faded"
            color="ruby"
            size="xs"
            icon="i-lucide-x"
            :is-loading="!!actingOn[item.id]"
            :disabled="!!actingOn[item.id]"
            :label="$t('METRICA_PENDING_APPROVALS.REJECT')"
            @click="reject(item)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
